import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Hash,
  Building2,
  Eye,
  Filter,
  MoreVertical,
  Calendar,
  User,
  ArrowUpDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChangeRequestDetailModal } from './ChangeRequestDetailModal';

// Using native Date formatting instead of date-fns to avoid dependency
const formatDate = (date: string | Date | null) => {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (date: string | Date | null) => {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

interface ChangeRequestListProps {
  workspaceId: string;
  projectId?: string;
  requestType?: string;
  onViewRequest?: (requestId: string) => void;
}

type SortField = 'created_at' | 'status' | 'priority' | 'requested_effective_date';
type SortDirection = 'asc' | 'desc';

export function ChangeRequestList({ workspaceId, projectId, requestType, onViewRequest }: ChangeRequestListProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['change-management-requests', workspaceId, projectId, requestType, statusFilter, priorityFilter],
    queryFn: async () => {
      let query = supabase
        .from('tum_change_management_requests')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (projectId) {
        query = query.eq('onboarding_project_id', projectId);
      }

      if (requestType) {
        query = query.eq('request_type', requestType);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (priorityFilter !== 'all') {
        query = query.eq('priority', priorityFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Sort the data
      const sorted = [...(data || [])].sort((a, b) => {
        let aVal: any = a[sortField];
        let bVal: any = b[sortField];
        
        if (sortField === 'created_at' || sortField === 'requested_effective_date') {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }
        
        if (sortField === 'priority') {
          const priorityOrder: Record<string, number> = { urgent: 1, high: 2, normal: 3, low: 4 };
          aVal = priorityOrder[aVal] || 5;
          bVal = priorityOrder[bVal] || 5;
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
      
      return sorted;
    },
    enabled: !!workspaceId,
  });

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'roll_in': return <ArrowRight className="h-4 w-4 text-blue-500" />;
      case 'rollout': return <ArrowRightLeft className="h-4 w-4 text-orange-500" />;
      case 'account_number_change': return <Hash className="h-4 w-4 text-purple-500" />;
      case 'site_disconnection': return <Building2 className="h-4 w-4 text-red-500" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon: typeof Clock, color: string }> = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      under_review: { variant: 'default', icon: Clock, color: 'text-blue-600' },
      approved: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      executed: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      failed: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      cancelled: { variant: 'secondary', icon: XCircle, color: 'text-gray-600' },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    const statusLabel = status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    return (
      <Badge variant={config.variant} className="gap-1.5">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {statusLabel}
      </Badge>
    );
  };

  const getRequestTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      roll_in: 'Roll In',
      rollout: 'Rollout',
      account_number_change: 'Account Number Change',
      site_disconnection: 'Site Disconnection',
    };
    return labels[type] || type;
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', color: string }> = {
      urgent: { variant: 'destructive', color: 'text-red-600' },
      high: { variant: 'default', color: 'text-orange-600' },
      normal: { variant: 'secondary', color: 'text-gray-600' },
      low: { variant: 'outline', color: 'text-gray-500' },
    };
    const config = variants[priority] || variants.normal;
    return (
      <Badge variant={config.variant} className="text-xs">
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  const getRequestDetails = (request: any) => {
    const data = request.request_data || {};
    switch (request.request_type) {
      case 'roll_in':
        return `New Account: ${data.new_account_number || 'N/A'}`;
      case 'rollout':
        return `Account: ${data.account_id || 'N/A'}`;
      case 'account_number_change':
        return `${data.old_account_number || 'N/A'} → ${data.new_account_number || 'N/A'}`;
      case 'site_disconnection':
        return `Site: ${data.site_id || 'N/A'}`;
      default:
        return request.description || 'No details';
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort(field)}>
      <div className="flex items-center gap-2">
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p>Loading change management requests...</p>
        </CardContent>
      </Card>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
          <p className="font-medium mb-1">No requests found</p>
          <p className="text-sm">Create a new request to get started.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Change Management Requests</CardTitle>
          <CardDescription>
            Manage account roll-ins, rollouts, number changes, and site disconnections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4 mb-4">
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
      </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="executed">Executed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <div className="ml-auto text-sm text-muted-foreground">
              Showing {requests.length} request{requests.length !== 1 ? 's' : ''}
            </div>
                  </div>
                  
          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Type</TableHead>
                  <SortableHeader field="created_at">Request Details</SortableHeader>
                  <TableHead>Status</TableHead>
                  <SortableHeader field="priority">Priority</SortableHeader>
                  <TableHead>Requested By</TableHead>
                  <SortableHeader field="requested_effective_date">Effective Date</SortableHeader>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request: any) => (
                  <TableRow key={request.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {getRequestTypeIcon(request.request_type)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{getRequestTypeLabel(request.request_type)}</div>
                        <div className="text-sm text-muted-foreground">
                          {getRequestDetails(request)}
                        </div>
                  {request.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {request.description}
                          </div>
                    )}
                  </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell>
                      {getPriorityBadge(request.priority)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {request.requested_by ? `User ${request.requested_by.slice(0, 8)}` : 'N/A'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.requested_effective_date ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span>{formatDate(request.requested_effective_date)}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(request.created_at)}
                </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedRequestId(request.id);
                            onViewRequest?.(request.id);
                          }}>
                  <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
              </div>
            </CardContent>
          </Card>

      {/* Detail Modal */}
      <ChangeRequestDetailModal
        requestId={selectedRequestId}
        isOpen={!!selectedRequestId}
        onClose={() => setSelectedRequestId(null)}
      />
    </div>
  );
}

